/**
 * @bos/portal3 — the SIX STAGE REVIEWS (batch 7): lifecycles, operations, rules,
 * permissions, audit, modules. One section of the workbench (the data-model review's
 * sibling), opened from the stage rail, answering "what did it decide here, and does
 * that match my business?" in the owner's language.
 *
 * THE DOOR (the A3/A4 batch — no longer read-only): each stage now has a published,
 * signature-bound approval request, a binding approval, and a history archive (register
 * A3), and its `guidance/<stage>.md` is consumed mechanically by the chain (A4). So the
 * screen carries the data-model review's two-sided door, in that screen's register:
 *   - APPROVE binds by signature to the exact output the chain published — a later
 *     re-derivation is detected, the approval stops binding, and the run pauses here
 *     again for a fresh look;
 *   - DISAGREE sends the person's own words into the stage's own channel. It states its
 *     cost BEFORE sending: a paid re-derivation, and a request, not a guarantee — the
 *     machine can come back unchanged, and the diff panel says exactly that ("you asked
 *     — and nothing changed") rather than dressing it as success;
 *   - the diff compares against the ARCHIVED baseline, never live bytes, and shows
 *     everything that moved — including what nobody asked for (re-derivation is
 *     non-deterministic; hiding the noise would be the lie);
 *   - NOTHING-TO-DECIDE is a real state (register C4): the chain derived, from the
 *     output's own rows, that no decision exists here — no door is faked over it.
 * THE SECOND DOOR (the A2 batch): five of these six stages now also carry an EXACT
 * vocabulary — a change the chain applies mechanically at that stage's boundary, costing
 * no derivation and guaranteed to land, next to the words door that costs one and can be
 * ignored. What is offered is read off the same bytes the screen renders (StageEdits.tsx).
 * The MODULES review carries NONE, and says so in words: nothing downstream reads that
 * artifact as a hash, and the pieces the app is built from are minted in the same breath,
 * so an exact change there would not reach the app. That is the honest answer, not a gap.
 *
 * HONESTY RULES (the data-model review's, kept):
 *   - Everything rendered is a projection of bytes the run wrote, shaped server-side
 *     (stage-artifacts.ts). No cost, no timings, no percentages, no severity the
 *     artifacts don't carry.
 *   - Machine reasons exist ONLY for lifecycles (sm-dossier; register B4) — they render
 *     verbatim, attached where they belong. The other five screens are barer, and that
 *     is honest rather than a gap papered over.
 *   - The RULES TRIAGE claims only what the server computed from the bytes: operations
 *     whose preconditions go beyond the structural kinds. When nothing in the bytes
 *     distinguishes rows (all structural, or all substantive), the screen SAYS SO and
 *     shows everything, rather than faking a severity that doesn't exist. The "show
 *     all" count is the list's real length.
 *   - Three non-ready states, each in words: not-yet-reached (per the rail), an
 *     artifact the run never wrote, and one this portal cannot make sense of.
 */
import { useEffect, useState } from 'react';
import type {
  AppEventDoc,
  ApproveStageRouteResult,
  AuditDoc,
  LifecycleMachineDoc,
  LifecyclesDoc,
  ModulesDoc,
  OperationDoc,
  OperationsDoc,
  PermissionsDoc,
  RequestStageRevisionResult,
  RevisionComment,
  RulePreconditionDoc,
  RulesDoc,
  RunStageDoorResult,
  StageApproval,
  StageDiff,
} from '@bos/portal3-server';
import type {
  ApplyStageBasketResult,
  OrderStageEditResult,
  RunStageBasketPreview,
  RunStageEditPreview,
  StageEditPatch,
} from './hooks';
import { runErrorMessage, type StageReviewPayload, type StageReviewStatus } from './hooks';
import { reviewStageLabel, STAGE_SUBJECTS, type RailState, type StageReviewKey } from './model';
import {
  machineAfterBasket,
  MOVE_SEP,
  permissionsInUse,
  ProseOnlyNote,
  StageEditDoor,
  type StageEditSeed,
} from './StageEdits';
import { LifecycleDiagram, type LifecycleGestures } from './LifecycleDiagram';
import { PermissionsMatrix, type MatrixGestures } from './PermissionsMatrix';

// ── shared bits ──────────────────────────────────────────────────────────────

function SectionHead({ title }: { title: string }) {
  return <h3 className="sr__section">{title}</h3>;
}

// ── lifecycles ───────────────────────────────────────────────────────────────

interface LifecyclesSectionProps {
  readonly doc: LifecyclesDoc;
  /** The exact door's basket so far — so a state or a move a person has composed but not yet
   *  applied is drawn as PENDING, never as something the machine derived. */
  readonly basket: readonly StageEditPatch[];
  /** null = the exact door is not open: the diagrams are read surfaces and offer no change. */
  readonly gesturesFor: ((entity: string) => LifecycleGestures) | null;
}

function MachineCard({ machine }: { machine: LifecycleMachineDoc }) {
  return (
    <article className="sr-machine" aria-label={machine.entity}>
      <h4 className="sr-machine__name">{machine.entity}</h4>
      <p className="sr-machine__states">
        {machine.states.join(' · ')}
        {machine.initial !== null && <span className="sr__meta"> — starts at {machine.initial}</span>}
        {machine.terminal.length > 0 && (
          <span className="sr__meta">
            {' '}
            — {machine.terminal.join(' and ')} {machine.terminal.length === 1 ? 'is' : 'are'} final
          </span>
        )}
      </p>
      {machine.transitions.length > 0 && (
        <ul className="sr-moves">
          {machine.transitions.map((t, i) => (
            <li key={i} className="sr-move">
              <span className="sr-move__line">
                {t.from} → {t.to}
                {t.event !== null && <span className="sr__meta"> on {t.event}</span>}
              </span>
              {t.guard !== null && <span className="sr-move__guard">only when: {t.guard}</span>}
              {t.enforcesRule !== null && (
                <span className="sr__meta">enforces {t.enforcesRule}</span>
              )}
            </li>
          ))}
        </ul>
      )}
      {machine.verified !== null && (
        <p className={`sr-machine__verified${machine.verified.agrees === false ? ' sr-machine__verified--flag' : ''}`}>
          {machine.verified.agrees === false && <strong>The machine flagged a disagreement here: </strong>}
          {machine.verified.reason}
        </p>
      )}
    </article>
  );
}

function LifecyclesSection({ doc, basket, gesturesFor }: LifecyclesSectionProps) {
  return (
    <>
      {doc.summary !== null && <p className="sr__summary">{doc.summary}</p>}
      <SectionHead title="What moves through states" />
      {/* AN APP WITH NO LIFECYCLES SHOWS NO DIAGRAM AND NO EMPTY FRAME — the sentence is the
          whole answer, and a picture of nothing would be worse than none. */}
      {doc.machines.length === 0 && (
        <p className="page__note">Nothing in this app moves through states — no lifecycles were derived.</p>
      )}
      {doc.machines.length > 0 && (
        <p className="sr__aside">
          {gesturesFor !== null
            ? 'Click a state to change it; drag one state onto another to allow a new move between them. Nothing is committed until you say so. '
            : ''}
          An arrow from nowhere is where it starts · a double ring is where it stops · ◆ on a move means
          it only happens when something is true — click it to read the condition.
        </p>
      )}
      <div className="sr-machines">
        {doc.machines.map((m) => (
          <div key={m.entity} className="sr-machine-block">
            <LifecycleDiagram
              entity={m.entity}
              machine={machineAfterBasket(m, basket)}
              gestures={gesturesFor !== null ? gesturesFor(m.entity) : null}
            />
            <MachineCard machine={m} />
          </div>
        ))}
      </div>
      {doc.skippedEnums.length > 0 && (
        <>
          <SectionHead title="Labels that never change (no lifecycle)" />
          <ul className="sr-list">
            {doc.skippedEnums.map((s) => (
              <li key={s.name} className="sr-list__row">
                <span className="sr-list__what">
                  {s.name}
                  {s.entity !== null && <span className="sr__meta"> on {s.entity}</span>}
                </span>
                <span className="sr-list__reason">{s.reason}</span>
              </li>
            ))}
          </ul>
        </>
      )}
      {doc.notes.length > 0 && (
        <>
          <SectionHead title="Also noted by the machine" />
          <ul className="sr-list">
            {doc.notes.map((n, i) => (
              <li key={i} className="sr-list__row">
                {n.about !== undefined && <span className="sr-list__what">{n.about}</span>}
                <span className="sr-list__reason">{n.reason}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  );
}

// ── operations ───────────────────────────────────────────────────────────────

function OperationRow({ op }: { op: OperationDoc }) {
  const meta: string[] = [];
  if (op.reads.length > 0) meta.push(`reads ${op.reads.join(', ')}`);
  if (op.writes.length > 0) meta.push(`writes ${op.writes.join(', ')}`);
  if (op.publishes.length > 0) meta.push(`announces ${op.publishes.join(', ')}`);
  if (op.returns.length > 0) {
    meta.push(
      `returns ${op.returns.map((r) => (r.of !== null ? `${r.name} (${r.many ? 'list of ' : ''}${r.of})` : r.name)).join(', ')}`,
    );
  }
  return (
    <li className="sr-op" aria-label={op.name}>
      <span className="sr-op__name">{op.name}</span>
      {op.summary !== null && <span className="sr-op__summary">{op.summary}</span>}
      {op.transition !== null && (
        <span className="sr-op__transition">
          {op.transition.from !== null
            ? `moves ${op.transition.entity}: ${op.transition.from} → ${op.transition.to}`
            : `creates ${op.transition.entity} as ${op.transition.to}`}
          {op.transition.event !== null && op.transition.event !== 'creation' && ` on ${op.transition.event}`}
        </span>
      )}
      {op.stateGuard !== null && <span className="sr-op__guard">only when: {op.stateGuard}</span>}
      {meta.length > 0 && <span className="sr__meta">{meta.join(' · ')}</span>}
    </li>
  );
}

function EventRow({ ev }: { ev: AppEventDoc }) {
  return (
    <li className="sr-event" aria-label={ev.eventType}>
      <span className="sr-event__name">{ev.eventType}</span>
      {ev.effect !== null && <span className="sr-event__effect">{ev.effect}</span>}
      {ev.publishedBy !== null && <span className="sr__meta">announced by {ev.publishedBy}</span>}
      {ev.reactions.length > 0 && (
        <ul className="sr-event__reactions">
          {ev.reactions.map((r, i) => (
            <li key={i}>
              in reaction, <strong>{r.operation}</strong>
              {r.note !== null && <span className="sr__meta"> — {r.note}</span>}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function OperationsSection({ doc }: { doc: OperationsDoc }) {
  // Group by the model's own kind words — 'query' and 'command' get owner headings, and
  // a kind this screen does not know gets its own group named verbatim (never dropped).
  const queries = doc.operations.filter((o) => o.kind === 'query');
  const commands = doc.operations.filter((o) => o.kind === 'command');
  const others = doc.operations.filter((o) => o.kind !== 'query' && o.kind !== 'command');
  const otherKinds = [...new Set(others.map((o) => o.kind))];
  return (
    <>
      {doc.summary !== null && <p className="sr__summary">{doc.summary}</p>}
      <p className="sr__lede">
        {doc.operations.length} operation{doc.operations.length === 1 ? '' : 's'} — this is the complete
        list of what the app will be able to do. Anything you didn’t ask for doesn’t belong here.
      </p>
      {commands.length > 0 && (
        <>
          <SectionHead title="Things the app can do" />
          <ul className="sr-ops">
            {commands.map((o) => (
              <OperationRow key={o.name} op={o} />
            ))}
          </ul>
        </>
      )}
      {queries.length > 0 && (
        <>
          <SectionHead title="Things you can look up" />
          <ul className="sr-ops">
            {queries.map((o) => (
              <OperationRow key={o.name} op={o} />
            ))}
          </ul>
        </>
      )}
      {otherKinds.map((kind) => (
        <div key={kind}>
          <SectionHead title={kind} />
          <ul className="sr-ops">
            {others
              .filter((o) => o.kind === kind)
              .map((o) => (
                <OperationRow key={o.name} op={o} />
              ))}
          </ul>
        </div>
      ))}
      {doc.events.length > 0 && (
        <>
          <SectionHead title="What happens in reaction to what" />
          <ul className="sr-events">
            {doc.events.map((e) => (
              <EventRow key={e.eventType} ev={e} />
            ))}
          </ul>
        </>
      )}
      {doc.workflows.length > 0 && (
        <>
          <SectionHead title="How the work flows" />
          <ul className="sr-list">
            {doc.workflows.map((w) => (
              <li key={w.name} className="sr-list__row">
                <span className="sr-list__what">{w.name}</span>
                <span className="sr__meta">{w.steps.join(' → ')}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  );
}

// ── rules ────────────────────────────────────────────────────────────────────

/** One precondition as an owner sentence — a rendering of the structured bytes, with the
 *  machine's own prose (custom descriptions) passed through verbatim. */
export function preconditionSentence(pc: RulePreconditionDoc): string {
  switch (pc.kind) {
    case 'entity-exists':
      if (pc.entity !== null && pc.idFrom !== null) return `the ${pc.entity} named by ${pc.idFrom} must exist`;
      return pc.entity !== null ? `the ${pc.entity} must exist` : 'the referenced record must exist';
    case 'state-in': {
      const allowed = pc.allowed.join(' or ');
      if (pc.entity !== null && pc.field !== null) return `${pc.entity} ${pc.field} must be ${allowed}`;
      return pc.field !== null ? `${pc.field} must be ${allowed}` : `the state must be ${allowed}`;
    }
    case 'numeric-range': {
      const field = pc.field ?? 'the value';
      if (pc.min !== null && pc.max !== null) return `${field} must be between ${pc.min} and ${pc.max}`;
      if (pc.min !== null) return `${field} must be at least ${pc.min}`;
      if (pc.max !== null) return `${field} must be at most ${pc.max}`;
      return `${field} is range-checked`;
    }
    case 'field-in-set': {
      const field = pc.field ?? 'the value';
      return pc.allowed.length > 0 ? `${field} must be one of ${pc.allowed.join(', ')}` : `${field} is checked against a fixed set`;
    }
    case 'custom':
      return pc.description ?? 'a machine-written rule (no description recorded)';
    default:
      // An unknown kind: name it verbatim and show whatever prose rides it — invent nothing.
      return pc.description !== null ? `${pc.kind}: ${pc.description}` : pc.kind;
  }
}

/** The triage's opening sentence — every number in it is the server-computed count. */
export function rulesTriageSentence(doc: RulesDoc): string {
  const total = doc.operations.length;
  if (total === 0) return 'No operation carries rules.';
  const m = doc.attentionCount;
  if (m === 0) {
    return (
      `Nothing here distinguishes one rule from another: every check is structural ` +
      `(the record exists; its state allows the action), and the artifact carries no severity. ` +
      `All ${total} are shown.`
    );
  }
  if (m === total) {
    return (
      `Every one of these ${total} operation${total === 1 ? "'s rules goes" : "s' rules go"} beyond the ` +
      `structural checks — nothing distinguishes them, so all are shown.`
    );
  }
  return (
    `The machine wrote rules for ${total} operations. ${m} of them go beyond structural checks ` +
    `(the record exists; its state allows the action) — start with those.`
  );
}

function RulesSection({ doc }: { doc: RulesDoc }) {
  const [showAll, setShowAll] = useState(false);
  const total = doc.operations.length;
  // The triage adds nothing when the bytes distinguish nothing — then everything shows.
  const triageUseful = doc.attentionCount > 0 && doc.attentionCount < total;
  const attention = doc.operations.filter((o) => o.needsAttention);
  const showingAll = !triageUseful || showAll;
  const listed = showingAll ? doc.operations : attention;

  return (
    <>
      {doc.summary !== null && <p className="sr__summary">{doc.summary}</p>}
      <p className="sr__lede">{rulesTriageSentence(doc)}</p>
      <ul className="sr-rules">
        {listed.map((o) => (
          <li key={o.operation} className={`sr-rule${o.needsAttention ? ' sr-rule--attention' : ''}`} aria-label={o.operation}>
            <span className="sr-rule__op">{o.operation}</span>
            <ul className="sr-rule__checks">
              {(showingAll ? o.preconditions : o.preconditions.filter((pc) => pc.substantive)).map((pc, i) => (
                <li key={i} className={pc.substantive ? 'sr-rule__check--substantive' : 'sr-rule__check--structural'}>
                  {preconditionSentence(pc)}
                </li>
              ))}
            </ul>
            {!showingAll && o.preconditions.some((pc) => !pc.substantive) && (
              <span className="sr__meta">
                plus {o.preconditions.filter((pc) => !pc.substantive).length} structural check
                {o.preconditions.filter((pc) => !pc.substantive).length === 1 ? '' : 's'}
              </span>
            )}
            {showingAll && o.errorCases.length > 0 && (
              <ul className="sr-rule__errors">
                {o.errorCases.map((ec, i) => (
                  <li key={i} className="sr__meta">
                    if {ec.when ?? 'the rule is broken'} → {ec.message ?? ec.code ?? 'refused'}
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
      {triageUseful && (
        <button className="btn btn--ghost btn--sm" type="button" onClick={() => setShowAll((v) => !v)}>
          {showAll ? `Back to the ${doc.attentionCount} that need a look` : `Show all ${total} operations`}
        </button>
      )}
      {doc.invariants.length > 0 && (
        <>
          <SectionHead title="Invariants" />
          <ul className="sr-list">
            {doc.invariants.map((inv, i) => (
              <li key={i} className="sr-list__row">
                {inv.name !== null && <span className="sr-list__what">{inv.name}</span>}
                {inv.text !== null && <span className="sr-list__reason">{inv.text}</span>}
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  );
}

// ── permissions ──────────────────────────────────────────────────────────────

function PermissionsSection({ doc, gestures }: { doc: PermissionsDoc; gestures: MatrixGestures | null }) {
  const loginRoles = doc.roles.filter((r) => r.canLogin !== false);
  const gridOps = doc.operations.filter((o) => o.permission !== null);
  const systemOps = doc.operations.filter((o) => o.authority === 'system');
  const justificationOf = new Map(doc.grants.map((g) => [g.role, g.justification]));
  // The matrix draws nothing when no operation asks for a permission, or there are no roles
  // (PermissionsMatrix returns null) — then its heading, its promise of a table "below", and
  // above all "click a cell" must not render over the nothing it would describe.
  const matrixDrawn = permissionsInUse(doc).length > 0 && doc.roles.length > 0;
  return (
    <>
      {doc.summary !== null && <p className="sr__summary">{doc.summary}</p>}
      {/* THE MATRIX FIRST — it answers "can a receptionist refund a payment?" in one glance, which
          no list of cards does. The cards stay below: they carry the model's own reasons. */}
      {matrixDrawn && (
        <>
          <SectionHead title="What each role is allowed" />
          <p className="sr__aside">
            Every permission this app actually uses, and who holds it.{' '}
            {gestures !== null ? 'Click a cell to give it or take it away — nothing is committed until you say so. ' : ''}
            Who may do each thing follows from this, and is written out further down.
          </p>
          <PermissionsMatrix doc={doc} gestures={gestures} />
        </>
      )}
      <SectionHead title="Who works in this app" />
      <div className="sr-roles">
        {doc.roles.map((r) => (
          <article key={r.id} className="sr-role" aria-label={r.id}>
            <h4 className="sr-role__name">
              {r.id}
              {r.canLogin === false && <span className="sr-chip">no login</span>}
            </h4>
            {r.summary !== null && <p className="sr-role__summary">{r.summary}</p>}
            {justificationOf.get(r.id) != null && (
              <p className="sr-role__why">{justificationOf.get(r.id)}</p>
            )}
          </article>
        ))}
      </div>
      {gridOps.length > 0 && loginRoles.length > 0 && (
        <>
          <SectionHead title="Who may do what" />
          <table className="sr-grid" aria-label="Who may do what">
            <thead>
              <tr>
                <th scope="col">Operation</th>
                {loginRoles.map((r) => (
                  <th key={r.id} scope="col">
                    {r.id}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {gridOps.map((o) => (
                <tr key={o.operation}>
                  <th scope="row">
                    {o.operation}
                    {o.rowScopeCount > 0 && <span className="sr-chip">row-limited</span>}
                  </th>
                  {loginRoles.map((r) => (
                    <td key={r.id} className={o.allowedRoles.includes(r.id) ? 'sr-grid__yes' : 'sr-grid__no'}>
                      {o.allowedRoles.includes(r.id) ? '✓' : '—'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      {systemOps.length > 0 && (
        <>
          <SectionHead title="Runs by itself" />
          <ul className="sr-list">
            {systemOps.map((o) => (
              <li key={o.operation} className="sr-list__row">
                <span className="sr-list__what">{o.operation}</span>
                <span className="sr__meta">the machine triggers this — no person does</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  );
}

// ── audit ────────────────────────────────────────────────────────────────────

function AuditSection({ doc }: { doc: AuditDoc }) {
  return (
    <>
      {doc.summary !== null && <p className="sr__summary">{doc.summary}</p>}
      <SectionHead title="What gets written down" />
      {doc.operations.length === 0 && <p className="page__note">Nothing is audited.</p>}
      <ul className="sr-list">
        {doc.operations.map((o) =>
          o.entries.map((e, i) => (
            <li key={`${o.operation}-${i}`} className="sr-list__row" aria-label={e.action}>
              <span className="sr-list__what">
                {o.operation} → {e.action}
              </span>
              <span className="sr__meta">
                {e.fields.length > 0 && `keeps ${e.fields.join(', ')}`}
                {e.includesActor === true && (e.fields.length > 0 ? ' · records who did it' : 'records who did it')}
              </span>
            </li>
          )),
        )}
      </ul>
      {doc.exclusions.length > 0 && (
        <>
          <SectionHead title="What is deliberately NOT recorded" />
          <p className="sr__lede">
            This is the half worth reading: each of these leaves no trace, for the machine’s own stated
            reason. If your business needs any of them on paper, that is a data-model conversation.
          </p>
          <ul className="sr-list sr-list--exclusions">
            {doc.exclusions.map((x, i) => (
              <li key={i} className="sr-list__row">
                <span className="sr-list__what">
                  {x.operation}
                  {x.entity !== null && <span className="sr__meta"> ({x.entity})</span>}
                </span>
                {x.reason !== null && <span className="sr-list__reason">{x.reason}</span>}
              </li>
            ))}
          </ul>
        </>
      )}
      {doc.taxonomy.length > 0 && (
        <>
          <SectionHead title="What a record always carries" />
          <ul className="sr-list">
            {doc.taxonomy.map((t) => (
              <li key={t.entityType} className="sr-list__row">
                <span className="sr-list__what">{t.entityType}</span>
                <span className="sr__meta">{t.fields.join(', ')}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  );
}

// ── modules ──────────────────────────────────────────────────────────────────

function ModulesSection({ doc }: { doc: ModulesDoc }) {
  const unplacedAny =
    doc.unplaced.entities.length > 0 || doc.unplaced.operations.length > 0 || doc.unplaced.events.length > 0;
  return (
    <>
      <p className="sr__lede">
        The app is split into {doc.modules.length} part{doc.modules.length === 1 ? '' : 's'}. Each part owns
        its own records and operations — a change in one stays in one.
      </p>
      <div className="sr-modules">
        {doc.modules.map((m) => (
          <article key={m.slug} className="sr-module" aria-label={m.name ?? m.slug}>
            <h4 className="sr-module__name">{m.name ?? m.slug}</h4>
            {m.description !== null && <p className="sr-module__desc">{m.description}</p>}
            {m.entities.length > 0 && (
              <p className="sr-module__owns">
                <span className="sr__meta">keeps track of:</span> {m.entities.join(', ')}
              </p>
            )}
            {m.operations.length > 0 && (
              <p className="sr-module__owns">
                <span className="sr__meta">can do:</span> {m.operations.join(', ')}
              </p>
            )}
            {m.events.length > 0 && (
              <p className="sr-module__owns">
                <span className="sr__meta">announces:</span> {m.events.join(', ')}
              </p>
            )}
          </article>
        ))}
      </div>
      {unplacedAny && (
        <>
          <SectionHead title="Placed in no part" />
          <p className="sr__lede">These name a part the split does not declare — worth a question.</p>
          <ul className="sr-list">
            {[...doc.unplaced.entities, ...doc.unplaced.operations, ...doc.unplaced.events].map((x) => (
              <li key={x} className="sr-list__row">
                <span className="sr-list__what">{x}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  );
}

// ── the door (the A3/A4 batch: approval + disagree + diff) ───────────────────

/** The "nothing to decide here" sentence, per stage — MIRROR of the chain's own owner
 *  notes (`@bos/newpip` stage-approval.ts `stageNothingToDecideOwnerNote`), cited. */
export function nothingToDecideSentence(stage: StageReviewKey): string {
  switch (stage) {
    case 'statemachine':
      return 'Nothing in this app moves through states, so there are no lifecycles to approve here.';
    case 'process':
      return 'No operations were derived, so there is nothing to approve here.';
    case 'rules':
      return 'Every rule here just restates the data model you already approved, so there is nothing new to decide.';
    case 'rbac':
      return 'This app has no separate roles, so there are no permissions to decide.';
    case 'audit':
      return 'Nothing is recorded and nothing was declared off the record, so there is no audit plan to decide.';
    case 'decomposition':
      return 'The app is one part, so there is no split into modules to decide.';
  }
}

/** The door's opening sentence, per the state/reason the chain published for this pause. */
export function stageDoorSentence(stage: StageReviewKey, approval: StageApproval): string {
  const { subject, plural } = STAGE_SUBJECTS[stage];
  if (approval.state === 'stale') {
    return 'Your recorded approval no longer matches what is on offer — read it again and re-approve.';
  }
  if (approval.state !== 'awaiting') return '';
  if (approval.reason === 'superseded') {
    return plural
      ? `Your earlier approval no longer applies — the ${subject} have been rebuilt since you approved them. Have a look at the new ones and approve them again.`
      : `Your earlier approval no longer applies — the ${subject} has been rebuilt since you approved it. Have a look at the new one and approve it again.`;
  }
  if (approval.reason === 'unsigned') {
    return plural
      ? `Your earlier approval could not be matched to these ${subject}, so it no longer applies. Have a look at what we derived and approve them again.`
      : `Your earlier approval could not be matched to this ${subject}, so it no longer applies. Have a look at what we derived and approve it again.`;
  }
  return plural
    ? `These are the ${subject} the machine derived. Read them, and approve them to continue — nothing more is spent while it waits.`
    : `This is the ${subject} the machine derived. Read it, and approve it to continue — nothing more is spent while it waits.`;
}

/**
 * ██ YOU ASKED, AND YOUR REQUEST WAS NOT APPLIED ██ (the known gap: stamped since RUN-007,
 * rendered nowhere until now).
 *
 * When the owner disagrees with a stage in words, the machine derives it again — paid work,
 * and a request rather than a guarantee. The chain then CHECKS the new output against the
 * element the owner named: if that element came back byte-identical (prose re-wording
 * stripped), the order was ignored, and the chain stamps `revisionUnapplied` on the approval
 * request with the names of what it ignored.
 *
 * Before this, the door re-offered the unchanged shape as if it were a fresh answer, and only
 * a hand diff of the bytes showed the owner had been overruled by a model's habit. That is the
 * exact failure this product refuses everywhere else, so it says it in the first person the
 * owner reads, above the offer — and repeats the machine's own words underneath, unedited.
 */
function RevisionUnapplied({ stage, targets }: { stage: StageReviewKey; targets: readonly string[] }) {
  if (targets.length === 0) return null;
  const { subject } = STAGE_SUBJECTS[stage];
  return (
    <section className="sr-unapplied" role="alert" aria-label="Your request was not applied">
      <h3 className="sr-unapplied__title">You asked — and your request was not applied</h3>
      <p className="sr-unapplied__sentence">
        You paid for the {subject} to be derived again around your words, and{' '}
        {targets.length === 1 ? 'the part you named came' : 'the parts you named came'} back
        exactly as {targets.length === 1 ? 'it' : 'they'} were. That is not the machine
        agreeing with you — it is the machine not doing what you asked, and the check that
        compares the two caught it.
      </p>
      <ul className="sr-unapplied__list">
        {targets.map((t) => (
          <li key={t}>
            <code>{t}</code> — unchanged since you asked
          </li>
        ))}
      </ul>
      <p className="sr-unapplied__sentence">
        Your order has been written back into the machine’s instructions as binding, so a
        resume derives the {subject} again with it in front of them. You can also approve what
        is on offer as it stands, or say it differently below — nothing here is decided for
        you.
      </p>
    </section>
  );
}

/** The sentence the approve outcome earns — each one a fact the server reported. */
export function stageApproveResultSentence(result: ApproveStageRouteResult): string {
  if (result.resumed) return 'Approved — the machine is continuing.';
  if (result.alreadyApproved) return 'This exact version was already approved — nothing changed.';
  return 'Approved and recorded. The machine continues once every review at this stop is settled.';
}

/** The sentence a revision submission earns — each one a fact the server reported. */
export function stageRevisionResultSentence(stage: StageReviewKey, result: RequestStageRevisionResult): string {
  const { subject } = STAGE_SUBJECTS[stage];
  if (result.reason === 'already-recorded') {
    return 'Those words are already recorded — the machine has them. Nothing new was sent.';
  }
  if (result.resumed) return `Your words are recorded — the machine is deriving the ${subject} again.`;
  return `Your words are recorded — the machine will derive the ${subject} again the next time it runs.`;
}

/** The person's request, quoted back beside what it came to (the data-model panel's idiom). */
function StageCommentsQuote({ comments }: { comments: readonly RevisionComment[] }) {
  if (comments.length === 0) return null;
  return (
    <div className="sr-diff__asked">
      <span className="sr-diff__asked-head">You asked:</span>
      <ul className="sr-diff__asked-list">
        {comments.map((c, i) => (
          <li key={i}>
            {c.about !== undefined && <span className="sr-diff__asked-about">{c.about}: </span>}
            <span className="sr-diff__asked-text">“{c.text}”</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The generic changed-rows renderer: every string a server-side projection of bytes. */
function StageDiffSections({ diff }: { diff: StageDiff }) {
  return (
    <div className="sr-diff__sections">
      {diff.sections.map((s) => (
        <div key={s.title} className="sr-diff__section">
          <h4 className="sr-diff__section-title">{s.title}</h4>
          <ul className="sr-diff__list">
            {s.added.map((r, i) => (
              <li key={`a${i}`} className="sr-diff__row sr-diff__row--added">
                <span className="sr-diff__label">new: {r.label}</span>
                {r.detail !== '' && <span className="sr__meta">{r.detail}</span>}
              </li>
            ))}
            {s.removed.map((r, i) => (
              <li key={`r${i}`} className="sr-diff__row sr-diff__row--removed">
                <span className="sr-diff__label">gone: {r.label}</span>
                {r.detail !== '' && <span className="sr__meta">{r.detail}</span>}
              </li>
            ))}
            {s.changed.map((c, i) => (
              <li key={`c${i}`} className="sr-diff__row sr-diff__row--changed">
                <span className="sr-diff__label">{c.label}</span>
                <span className="sr__meta">was: {c.before}</span>
                <span className="sr__meta">now: {c.after}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

/** What the person's revision came to — every state its own plain sentence (the
 *  data-model DiffPanel's states, this stage's words). */
export function StageDiffPanel({ stage, diff }: { stage: StageReviewKey; diff: RunStageDoorResult['diff'] }) {
  const { subject, plural } = STAGE_SUBJECTS[stage];
  if (diff.state === 'none') return null;
  if (diff.state === 'pending') {
    return (
      <section className="sr-diff sr-diff--pending" aria-label="Your requested changes">
        <h3 className="sr-diff__title">Your requested changes</h3>
        <StageCommentsQuote comments={diff.comments} />
        <p className="sr-diff__sentence">
          Your words are recorded — the machine has not derived the new {subject} yet.
        </p>
      </section>
    );
  }
  if (diff.state === 'unchanged') {
    return (
      <section className="sr-diff sr-diff--unchanged" aria-label="Nothing changed">
        <h3 className="sr-diff__title">You asked — and nothing changed</h3>
        <StageCommentsQuote comments={diff.comments} />
        <p className="sr-diff__sentence">
          The machine derived the {subject} again with your words in front of it, and nothing moved.
          Your request was not turned into a change. If it matters, say it differently — or approve{' '}
          {plural ? 'them' : 'it'} as {plural ? 'they stand' : 'it stands'}.
        </p>
      </section>
    );
  }
  if (diff.state === 'closed') {
    return (
      <section className="sr-diff sr-diff--closed" aria-label="Changed">
        <h3 className="sr-diff__title">The {subject} changed after your request</h3>
        <StageCommentsQuote comments={diff.comments} />
        <p className="sr-diff__sentence">
          The run moved on before this comparison could be captured, so there is no honest diff to
          show here — what is below is what the run continued with.
        </p>
      </section>
    );
  }
  return (
    <section className="sr-diff sr-diff--changed" aria-label="What changed">
      <h3 className="sr-diff__title">What changed since the version you read</h3>
      <StageCommentsQuote comments={diff.comments} />
      <p className="sr-diff__sentence">
        This is everything that moved — including anything you did not ask about.
      </p>
      <StageDiffSections diff={diff.diff} />
    </section>
  );
}

// ── the section itself ───────────────────────────────────────────────────────

export interface StageReviewProps {
  readonly stage: StageReviewKey;
  /** Where the rail says this stage is (the journal's truth) — picks the honest sentence
   *  for an absent artifact: not-yet-reached vs ran-but-wrote-nothing. */
  readonly railState: RailState | null;
  readonly payload: StageReviewPayload | null;
  readonly status: StageReviewStatus;
  readonly error: string | null;
  /** The stage's door payload (approval + diff); null while it has not loaded. */
  readonly door: RunStageDoorResult | null;
  /** Is the run actually waiting at this stage's review right now (status says so AND
   *  disk says so — the data-model door's double gate)? */
  readonly doorOpen: boolean;
  readonly onApprove: () => Promise<ApproveStageRouteResult>;
  readonly onRequestRevision: (comments: RevisionComment[]) => Promise<RequestStageRevisionResult>;
  /** The EXACT door (register A2). Present on the five stages whose bytes carry a vocabulary; the
   *  modules review renders the prose-only note instead, and never calls these. */
  readonly onPreviewEdit: (edit: StageEditPatch) => Promise<RunStageEditPreview>;
  readonly onOrderEdit: (edit: StageEditPatch) => Promise<OrderStageEditResult>;
  readonly onPreviewBasket: (edits: StageEditPatch[]) => Promise<RunStageBasketPreview>;
  readonly onApplyBasket: (edits: StageEditPatch[]) => Promise<ApplyStageBasketResult>;
}

/** The honest sentence for an artifact that is not on disk, picked by the rail's state. */
export function absentSentence(railState: RailState | null): string {
  if (railState === 'done') {
    return 'The run went through this stage, but left nothing on disk to read here.';
  }
  if (railState === 'current' || railState === 'waiting') {
    return 'The machine is at this stage now — nothing has been written yet. This review fills in when it finishes.';
  }
  return 'The machine hasn’t reached this stage yet. This review appears as soon as it does.';
}

export const UNINTELLIGIBLE_SENTENCE =
  'This stage wrote something this screen can’t read — the file is there, but not in a shape this ' +
  'portal understands. If the machine is still working, it may be mid-write; check again shortly.';

/**
 * ██ THE PICTURES' WIRING ██ — what the two stages that have a natural PICTURE need beyond their
 * bytes: the exact door's basket (so pending is drawn as pending) and the gestures, which exist
 * only while that door is actually open. Both null/empty everywhere else, so the other four
 * stages render byte-identically to before.
 */
interface PictureWiring {
  readonly basket: readonly StageEditPatch[];
  readonly lifecycle: ((entity: string) => LifecycleGestures) | null;
  readonly matrix: MatrixGestures | null;
}

function body(payload: StageReviewPayload, railState: RailState | null, picture: PictureWiring) {
  const { read } = payload;
  if (read.state === 'absent') return <p className="page__note">{absentSentence(railState)}</p>;
  if (read.state === 'unintelligible') return <p className="page__note">{UNINTELLIGIBLE_SENTENCE}</p>;
  switch (payload.stage) {
    case 'statemachine':
      return <LifecyclesSection doc={payload.read.doc!} basket={picture.basket} gesturesFor={picture.lifecycle} />;
    case 'process':
      return <OperationsSection doc={payload.read.doc!} />;
    case 'rules':
      return <RulesSection doc={payload.read.doc!} />;
    case 'rbac':
      return <PermissionsSection doc={payload.read.doc!} gestures={picture.matrix} />;
    case 'audit':
      return <AuditSection doc={payload.read.doc!} />;
    case 'decomposition':
      return <ModulesSection doc={payload.read.doc!} />;
    default: {
      const exhaustive: never = payload;
      throw new Error(`unknown stage payload: ${String(exhaustive)}`);
    }
  }
}

export function StageReview({
  stage,
  railState,
  payload,
  status,
  error,
  door,
  doorOpen,
  onApprove,
  onRequestRevision,
  onPreviewEdit,
  onOrderEdit,
  onPreviewBasket,
  onApplyBasket,
}: StageReviewProps) {
  const label = reviewStageLabel(stage);
  const { subject } = STAGE_SUBJECTS[stage];
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [reviseNote, setReviseNote] = useState<string | null>(null);

  const approval = door?.approval ?? null;
  const open = doorOpen && approval !== null && (approval.state === 'awaiting' || approval.state === 'stale');

  // ── THE PICTURES (phase 2) ────────────────────────────────────────────────────────────
  // A gesture on a diagram or a matrix compiles to exactly one existing `sm-*` / `rb-*` change
  // and opens the SAME exact door below, prefilled. Nothing here sends anything anywhere: the
  // door's own preview and its own two buttons decide everything, exactly as they do by hand.
  const [seed, setSeed] = useState<StageEditSeed | null>(null);
  /** The door's basket, REPORTED upward so the pictures can mark pending members as pending. */
  const [stageBasket, setStageBasket] = useState<readonly StageEditPatch[]>([]);

  function gesture(
    kind: StageEditPatch['kind'],
    values: Record<string, string>,
    opts: { readonly autoPreview?: boolean; readonly focus?: string } = {},
  ) {
    setSeed((prior) => ({
      kind,
      values,
      ...(opts.autoPreview === true ? { autoPreview: true } : {}),
      ...(opts.focus !== undefined ? { focus: opts.focus } : {}),
      token: (prior?.token ?? 0) + 1,
    }));
  }

  /* The exact door is where every gesture lands, so a gesture exists only where that door does —
     the same condition its own render uses below. A closed door draws the same pictures with
     nothing to press, exactly as the cards do. */
  const exactDoorOpen =
    open &&
    door !== null &&
    door.exactDoor &&
    status === 'ready' &&
    payload !== null &&
    payload.stage === stage &&
    payload.read.state === 'ready';

  /* THE DOOR CLOSING TAKES THE GESTURE STATE WITH IT. The seed and the reported basket are
     facts about an OPEN exact door; if they outlived it, the next halt would replay the last
     gesture, and the lifecycle diagram would keep drawing "in the basket" pills for a basket
     that no longer exists anywhere (the door approved without applying leaves no UI to clear
     them). Closed door ⇒ both gone. */
  useEffect(() => {
    if (!exactDoorOpen) {
      setSeed(null);
      setStageBasket([]);
    }
  }, [exactDoorOpen]);

  const picture: PictureWiring = {
    basket: stageBasket,
    lifecycle:
      exactDoorOpen && stage === 'statemachine'
        ? (entity: string): LifecycleGestures => ({
            onRenameState: (state) => gesture('sm-state-rename', { entity, from: state }, { focus: 'to' }),
            onRemoveState: (state) => gesture('sm-state-remove', { entity, state }, { autoPreview: true }),
            onSetTerminal: (state, terminal) =>
              gesture('sm-terminal-set', { entity, state, terminal: terminal ? 'yes' : 'no' }, { focus: 'state' }),
            onAddTransition: (from, to) =>
              gesture('sm-transition-add', { entity, from, to }, { focus: 'event' }),
            onRemoveTransition: (from, to, event) =>
              gesture(
                'sm-transition-remove',
                { entity, move: [from, to, event].join(MOVE_SEP) },
                { autoPreview: true },
              ),
          })
        : null,
    matrix:
      exactDoorOpen && stage === 'rbac'
        ? {
            onGrantAdd: (role, permission) => gesture('rb-grant-add', { role, permission }, { autoPreview: true }),
            onGrantRemove: (role, permission) =>
              gesture('rb-grant-remove', { role, permission }, { autoPreview: true }),
            onRoleRemove: (role) => gesture('rb-role-remove', { role }, { autoPreview: true }),
          }
        : null,
  };

  async function approve() {
    setBusy(true);
    setNote(null);
    try {
      setNote(stageApproveResultSentence(await onApprove()));
    } catch (err) {
      setNote(runErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function submitRevision() {
    setBusy(true);
    setReviseNote(null);
    try {
      const result = await onRequestRevision([{ text: draft }]);
      setReviseNote(stageRevisionResultSentence(stage, result));
      if (result.recorded > 0) setDraft('');
    } catch (err) {
      setReviseNote(runErrorMessage(err));
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  }

  return (
    <section className="sr" aria-label={label}>
      <div className="sr__head">
        <h2 className="sr__title">{label}</h2>
      </div>

      {/* ── the door area: every approval state its own honest sentence ── */}
      {approval !== null && approval.state === 'none' && (
        <p className="sr__readonly" role="note">
          The machine didn’t collect an approval for this stage on this run — everything here was
          derived from the model you approved.
        </p>
      )}
      {approval !== null && approval.state === 'nothing-to-decide' && (
        <p className="sr__nothing" role="note">
          Nothing to decide here — {nothingToDecideSentence(stage)} No approval is asked, and the
          run does not stop for one.
        </p>
      )}
      {approval !== null && approval.state === 'approved' && (
        <p className="sr-approved" role="note">
          You approved the {subject}
          {approval.approvedAt !== null ? ` on ${new Date(approval.approvedAt).toLocaleString()}` : ''}. The
          approval is tied to this exact version — if it is rebuilt later, the run will pause here
          again for a fresh look.
        </p>
      )}
      {approval !== null && (approval.state === 'awaiting' || approval.state === 'stale') && !doorOpen && (
        <p className="sr__readonly" role="note">
          This stage is waiting for your approval — the run stops here when it next runs.
        </p>
      )}
      {/* ABOVE THE OFFER, never below it: an ignored order changes what approving means. */}
      {approval !== null && (approval.state === 'awaiting' || approval.state === 'stale') && (
        <RevisionUnapplied stage={stage} targets={approval.revisionUnapplied ?? []} />
      )}

      {open && approval !== null && (
        <div className="sr-door" role="region" aria-label="Approval">
          <p className="sr-door__sentence">{stageDoorSentence(stage, approval)}</p>
          <div className="sr-door__actions">
            <button className="btn btn--primary" type="button" onClick={() => void approve()} disabled={busy}>
              {busy ? 'Approving…' : 'Approve and continue'}
            </button>
          </div>
          <p className="sr-door__aside">
            Something wrong? There are two ways to say so below.{' '}
            {door !== null && door.exactDoor
              ? 'An exact change is applied as written — the machine does not get a say, and it costs no thinking. '
              : ''}
            Saying it in your own words is paid work: the machine derives the {subject} again around them
            — that is a request, not a guarantee — and everything that builds on{' '}
            {STAGE_SUBJECTS[stage].plural ? 'them' : 'it'} is derived again too. Either way the run pauses
            here again so you can see exactly what changed.
          </p>
        </div>
      )}

      {/* The approve outcome OUTLIVES the door (the data-model screen's law). */}
      {note !== null && (
        <p className="sr-door__note" role="status">
          {note}
        </p>
      )}

      {/* What a submitted revision came to — pending / unchanged / changed / closed.
          Above the body, so the person sees what moved BEFORE re-approving. */}
      {door !== null && <StageDiffPanel stage={stage} diff={door.diff} />}

      {status === 'loading' && <p className="page__note">Reading what the machine derived…</p>}
      {status === 'error' && (
        <p className="form__error" role="alert">
          {error ?? 'Something went wrong reading this stage.'}
        </p>
      )}
      {status === 'ready' && payload !== null && payload.stage === stage && body(payload, railState, picture)}

      {/* ── THE EXACT DOOR (register A2) ── the order, above the request. Rendered only where the
          stage's bytes honestly carry a vocabulary; the modules review gets the prose-only note. ── */}
      {open && status === 'ready' && payload !== null && payload.stage === stage && payload.read.state === 'ready' && (
        door !== null && door.exactDoor ? (
          <StageEditDoor
            stage={stage}
            payload={payload}
            rederives={door.rederives}
            onPreview={onPreviewEdit}
            onOrder={onOrderEdit}
            onPreviewBasket={onPreviewBasket}
            onApplyBasket={onApplyBasket}
            seed={seed}
            onSeedConsumed={() => setSeed(null)}
            onBasketChange={setStageBasket}
          />
        ) : (
          <ProseOnlyNote stage={stage} />
        )
      )}

      {/* ── the disagree door (the prose channel the chain consumes) ── */}
      {open && (
        <div className="sr-revise" role="region" aria-label="Ask for changes">
          <h3 className="sr__section">Something wrong?</h3>
          <label className="sr-revise__label" htmlFor={`sr-revise-${stage}`}>
            Say it in your own words — what should be different about the {subject}?
          </label>
          <textarea
            id={`sr-revise-${stage}`}
            className="sr-revise__text"
            rows={3}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          {draft.trim() !== '' && !confirming && (
            <div className="sr-revise__actions">
              <button className="btn btn--ghost" type="button" onClick={() => setConfirming(true)} disabled={busy}>
                Ask for changes
              </button>
            </div>
          )}
          {draft.trim() !== '' && confirming && (
            <div className="sr-revise__confirm" role="region" aria-label="Before it runs">
              <p className="sr-revise__consequence">
                Before it runs: the machine will derive the {subject} again with your words in front
                of it. Everything that builds on this stage is derived again too — that is paid
                work. Your words are a request, not a guarantee: the machine can come back
                unchanged, and this screen will say so. The run pauses here again so you can see
                exactly what changed before approving.
              </p>
              <div className="sr-revise__actions">
                <button className="btn btn--primary" type="button" onClick={() => void submitRevision()} disabled={busy}>
                  {busy ? 'Sending…' : `Re-derive the ${subject}`}
                </button>
                <button className="btn btn--quiet" type="button" onClick={() => setConfirming(false)} disabled={busy}>
                  Keep reading
                </button>
              </div>
            </div>
          )}
          {reviseNote !== null && (
            <p className="sr-door__note" role="status">
              {reviseNote}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
